import Typography from '@mui/material/Typography'
import dayjs from 'dayjs'
import { useParams } from 'react-router'
import { useAllAgreementsV8 } from '../api/react-query'
import {
  type AgreementStatus,
  type Amount,
  type AuthenticationMethod,
  type BackOfficeAgreementPersonV8,
  BackOfficeAgreementTypeV8,
  type BackOfficeMinimumPaymentV8,
  type BackOfficePaymentFrequencyV8,
  type BackOfficePaymentMethodV8,
  type CardCreditCompanyGuarantorV8,
  type DeliveryMethod,
} from '../api/swagger/definitions/backoffice'
import IconExport24LinkGreen from '../figma/images/iconExport24LinkGreen'
import Spacings from '../figma/tokens/Spacings'
import { BorderContainer } from '../helpers/CreditOnCardHelpers'
import TextKeys from '../libs/TextKeys'
import { YYYY_MM_DD } from '../mynt-components/Constants'
import FigmaBox from '../mynt-components/components/FigmaBox'
import FigmaTextLink from '../mynt-components/components/FigmaTextLink'
import DirectDebitTabStatusTag from './DirectDebitTabStatusTag'
import TextContainer from './TextContainer'

interface BackOfficeCardCreditAgreementResponseDto {
  id: string
  customerId: string
  agreementStatus: AgreementStatus
  sendDate?: string
  externalLink?: string
  maxLimit: Amount
  paymentFrequency: BackOfficePaymentFrequencyV8
  paymentTerm: number
  minimumPayment: BackOfficeMinimumPaymentV8
  interestRate: number
  fixedFee: number
  paymentMethod: BackOfficePaymentMethodV8
  signatoriesData: Array<BackOfficeAgreementPersonV8>
  personalGuarantorsData: Array<BackOfficeAgreementPersonV8>
  companyGuarantors: Array<CardCreditCompanyGuarantorV8>
  otherTerms?: string
  deliveryMethod: DeliveryMethod
  authenticationMethod: AuthenticationMethod
  mandateReference?: string
  bankAccount?: string
  iban?: string
}

interface BackOfficeDirectDebitAgreementResponseDto {
  id: string
  customerId: string
  agreementStatus: AgreementStatus
  sendDate?: string
  mandateReference?: string
  iban?: string
  bankAccount?: string
  externalLink?: string
}

const DirectDebitTabMandateAgreements = () => {
  const { customerId } = useParams<'customerId'>()
  const { data: agreements = [] } = useAllAgreementsV8(customerId || '', [BackOfficeAgreementTypeV8.DIRECT_DEBIT_AGREEMENT], {
    enabled: !!customerId,
  })

  const prettyDate = (date?: string) => {
    if (!date) return ''
    return dayjs(date).format(YYYY_MM_DD)
  }

  return (
    <BorderContainer style={{ display: 'flex', flexBasis: 'fit-content' }} fullWidth smallSpacing>
      <FigmaBox fullWidth bottom={Spacings.medium}>
        <TextContainer textKey={TextKeys.earlierCreditAgreementHeadline} text={'Mandate agreements'} />
      </FigmaBox>

      {agreements?.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Customer has no mandate agreements
        </Typography>
      )}

      <FigmaBox fullWidth gap={Spacings.tiny}>
        {agreements?.map((agreement) => (
          <FigmaBox key={agreement.id} fullWidth direction="row" justify="space-between" align="center">
            <FigmaBox gap={Spacings.smallest}>
              {agreement.externalLink && (
                <FigmaTextLink
                  textKey={TextKeys.earlierCreditAgreementNumberLink}
                  text={agreement.mandateReference}
                  url={agreement.externalLink}
                  rightSvg={() => (
                    <FigmaBox spacing={Spacings.minimum} left>
                      <IconExport24LinkGreen />
                    </FigmaBox>
                  )}
                />
              )}
              <TextContainer textKey={TextKeys.earlierCreditAgreementDate} text={prettyDate(agreement.sendDate)} />
            </FigmaBox>

            <DirectDebitTabStatusTag status={agreement.agreementStatus} />
          </FigmaBox>
        ))}
      </FigmaBox>
    </BorderContainer>
  )
}

export default DirectDebitTabMandateAgreements
